import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, getDaysInMonth } from 'date-fns';
import { Lock, Loader2, ExternalLink, ChevronDown, ChevronRight, ChevronLeft, AlertTriangle, AlertCircle, CheckCircle2, ArrowUp, ArrowDown, Send, RefreshCw, Calendar, Users, Info, DollarSign, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import HamburgerMenu from '../components/layout/HamburgerMenu';
import StaffModal from '../components/ops/StaffModal';
import CommissionModal from '../components/payroll/CommissionModal';
import { PendingCommissions } from '../components/payroll/PendingCommissions';
import AlleanzaSettingsModal from './AlleanzaSettingsModal';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

const PAY_METHOD_LABELS = {
  wise: 'Wise',
  alleanza: 'Alleanza (US Payroll)',
  paypal: 'PayPal',
  bank_transfer: 'Bank Transfer',
  email_only: 'Email Only',
  owner_draw: 'Owner Draw',
};

function AlertBadge({ alert }) {
  if (!alert) return null;
  const cls = alert.severity === 'red' ? 'bg-red-50 text-red-700 border-red-200'
    : alert.severity === 'amber' ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const Icon = alert.severity === 'green' ? CheckCircle2 : AlertTriangle;
  return (
    <Badge variant="outline" className={`${cls} text-xs inline-flex items-center gap-1`} data-testid="alert-badge">
      <Icon className="h-3 w-3" />{alert.label}
    </Badge>
  );
}

function getWeekRange(refDate) {
  const d = new Date(refDate);
  const day = d.getDay();
  const sun = new Date(d);
  sun.setDate(d.getDate() - day);
  const sat = new Date(sun);
  sat.setDate(sun.getDate() + 6);
  return {
    start: format(sun, 'yyyy-MM-dd'),
    end: format(sat, 'yyyy-MM-dd'),
    label: `${format(sun, 'MMM d')} — ${format(sat, 'MMM d, yyyy')}`,
    days: 7,
  };
}

function getMonthRange(refDate) {
  const d = new Date(refDate);
  const first = startOfMonth(d);
  const last = endOfMonth(d);
  return {
    start: format(first, 'yyyy-MM-dd'),
    end: format(last, 'yyyy-MM-dd'),
    label: format(first, 'MMMM yyyy'),
    days: getDaysInMonth(d),
  };
}

export default function PayrollPage() {
  const { session } = useAuth();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [approvingEmail, setApprovingEmail] = useState(null);
  const [undoingEmail, setUndoingEmail] = useState(null);
  const [approvedSet, setApprovedSet] = useState(new Set());
  const [wiseSending, setWiseSending] = useState(false);
  const [wiseErrors, setWiseErrors] = useState({});
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Commission modal state
  const [commModalOpen, setCommModalOpen] = useState(false);
  const [commStaff, setCommStaff] = useState(null);
  const [alleanzaOpen, setAlleanzaOpen] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState(null); // { email, name, week_end }
  const [rollbackInput, setRollbackInput] = useState('');
  const [rollbackSending, setRollbackSending] = useState(false);

  // Pay period toggle & date navigation
  const [payPeriod, setPayPeriod] = useState('weekly');
  const [dateRef, setDateRef] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const range = payPeriod === 'monthly' ? getMonthRange(dateRef) : getWeekRange(dateRef);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await api.post('/api/ops/auth', { password });
      setAuthed(true);
      sessionStorage.setItem('payroll_auth', 'true');
    } catch { toast.error('Invalid password'); }
    finally { setAuthLoading(false); }
  };

  useEffect(() => {
    if (sessionStorage.getItem('payroll_auth') === 'true') setAuthed(true);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setApiError(null);
      setData(null);
      setExpandedId(null);
      const res = await api.get('/api/payroll/weekly', {
        params: { week_start: range.start, week_end: range.end, _t: Date.now() },
      });
      setData(res.data);
      // Refresh commStaff if the commission modal is open
      setCommStaff(prev => {
        if (!prev) return null;
        const fresh = (res.data.data || []).find(p => p.email === prev.email);
        return fresh || prev;
      });
      // Pre-populate approved set from backend (persists across refresh)
      const preApproved = new Set();
      for (const p of (res.data.data || [])) {
        if (p.approved_this_week) {
          preApproved.add(p.email);
        }
      }
      setApprovedSet(preApproved);

      if (res.data.warnings && res.data.warnings.length > 0) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || 'Failed to load payroll data';
      if (status === 502) {
        setApiError(msg);
        setData(null);
      } else {
        toast.error(msg);
      }
    } finally { setLoading(false); }
  }, [range.start, range.end]);

  useEffect(() => {
    if (authed && session?.access_token) load();
  }, [authed, session, load]);

  const prevPeriod = () => setDateRef(prev => {
    if (payPeriod === 'monthly') return subMonths(new Date(prev), 1);
    const d = new Date(prev); d.setDate(d.getDate() - 7); return d;
  });
  const nextPeriod = () => setDateRef(prev => {
    if (payPeriod === 'monthly') return addMonths(new Date(prev), 1);
    const d = new Date(prev); d.setDate(d.getDate() + 7); return d;
  });

  const handleApprove = async (p) => {
    setApprovingEmail(p.email);
    try {
      const res = await api.post(`/api/payroll/approve/${encodeURIComponent(p.email)}`, {
        week_start: range.start,
        week_end: range.end,
        staff_name: p.name,
        hours: p.hours,
        net_pay: p.net_amount || p.gross_wage,
        role: p.role,
        one_time_total: p.one_time_total || 0,
        recurring_total: p.recurring_total || 0,
        clawback_total: p.clawback_total || 0,
      });
      const d = res.data;
      const details = [];
      if (d.one_time_paid > 0) details.push(`${d.one_time_paid} commission(s) cleared`);
      if (d.clawbacks_decremented > 0) details.push(`${d.clawbacks_decremented} clawback(s) updated`);

      setApprovedSet(prev => {
        const next = new Set(prev);
        next.add(p.email);
        return next;
      });

      toast.success(`${p.name} approved${details.length > 0 ? ` (${details.join(', ')})` : ''}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to approve';
      toast.error(msg);
    } finally {
      setApprovingEmail(null);
    }
  };

  const handleUndo = async (p) => {
    setUndoingEmail(p.email);
    try {
      const res = await api.post(`/api/payroll/undo-approve/${encodeURIComponent(p.email)}`, {
        week_start: range.start,
      });
      const d = res.data;
      setApprovedSet(prev => {
        const next = new Set(prev);
        next.delete(p.email);
        return next;
      });
      // Clear any Wise error for this person
      setWiseErrors(prev => {
        const next = { ...prev };
        delete next[p.email];
        return next;
      });
      const details = [];
      if (d.commissions_restored > 0) details.push(`${d.commissions_restored} commission(s) restored`);
      if (d.clawbacks_reversed > 0) details.push(`${d.clawbacks_reversed} clawback(s) reversed`);
      toast.success(`Approval undone for ${p.name}${details.length > 0 ? ` (${details.join(', ')})` : ''}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to undo approval';
      toast.error(msg);
    } finally {
      setUndoingEmail(null);
    }
  };

  const handleSendForPayment = async () => {
    setConfirmModalOpen(false);
    const approvedRows = payroll.filter(p => approvedSet.has(p.email) && !p.is_paid && (p.net_amount || p.gross_wage) > 0);
    if (approvedRows.length === 0) {
      toast.error('No approved staff to process');
      return;
    }
    setWiseSending(true);
    try {
      const res = await api.post('/api/payroll/wise-batch', {
        week_start: range.start,
        week_end: range.end,
        staff: approvedRows.map(p => ({
          email: p.email,
          name: p.name,
          net_pay: p.net_amount || p.gross_wage,
          hours: p.hours,
          rate: p.rate || 0,
          role: p.role,
          pay_method: p.payment_method || 'wise',
          one_time_total: p.one_time_total || 0,
          recurring_total: p.recurring_total || 0,
          clawback_total: p.clawback_total || 0,
        })),
      });
      const d = res.data;

      // Log full response for debugging
      console.log('[Wise Batch] Full response:', JSON.stringify(d, null, 2));

      // Track per-staff errors for persistent UI display
      const newErrors = {};
      const errors = (d.results || []).filter(r => r.status === 'failed');
      if (errors.length > 0) {
        errors.forEach(e => {
          console.error('[Wise Batch Failure]', e.email, e.error, e.error_detail || '');
          toast.error(`${e.name}: ${e.error}`);
          newErrors[e.email] = e.error;
        });
      }
      setWiseErrors(prev => ({ ...prev, ...newErrors }));

      // Summary toast with batch/funding status
      const fundLabel = d.funding_status === 'staged' ? ' · Staged for funding' : d.funding_status === 'failed' ? ' · Funding staging failed' : '';
      if (d.failed > 0) {
        toast.warning(`Wise batch: ${d.drafted} drafted, ${d.failed} failed, ${d.emailed || 0} emailed${fundLabel}`);
      } else {
        toast.success(`Wise batch created & ${d.emailed || 0} statements emailed for ${d.drafted} staff${fundLabel}`);
      }
    } catch (err) {
      console.error('[Wise Batch] Request failed:', err.response?.data || err.message);
      const msg = err.response?.data?.detail || 'Wise batch failed';
      toast.error(msg);
    } finally {
      setWiseSending(false);
    }
  };

  const openCommissions = (p) => {
    setCommStaff(p);
    setCommModalOpen(true);
  };

  // Password gate
  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm shadow-[var(--shadow-2)]">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold" data-testid="payroll-auth-heading">Payroll Access</h2>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAuth} className="space-y-3">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" autoFocus data-testid="payroll-password-input" />
              <Button type="submit" className="w-full" disabled={authLoading || !password} data-testid="payroll-unlock-btn">
                {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Unlock
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allPayroll = data?.data || [];
  // Filter by active pay period toggle
  const payroll = allPayroll.filter(p => (p.pay_period || 'weekly') === payPeriod);
  const hasCommissionStaff = payroll.some(p => p.is_commission_eligible);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <h1 className="text-lg font-semibold tracking-tight" data-testid="payroll-heading">Weekly Payroll</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 px-2.5" onClick={() => setStaffModalOpen(true)} title="Manage staff roles and standard hours" data-testid="manage-staff-btn">
              <Users className="h-3.5 w-3.5 mr-1" /><span className="text-xs">Staff</span>
            </Button>
            <Button variant="outline" size="sm" className="h-8 px-2.5" onClick={() => setAlleanzaOpen(true)} title="Alleanza US Payroll settings">
              <span className="text-xs">Alleanza (US)</span>
            </Button>
            <a href="https://cmg.trackabi.com/timesheet" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="h-8 px-2.5" title="Open Trackabi Timesheet">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /><span className="text-xs">Trackabi</span>
              </Button>
            </a>
            <HamburgerMenu />
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <PendingCommissions staffList={payroll} />
        <div className="mt-3">
        <Card className="shadow-[var(--shadow-1)]">
          <CardHeader className="pb-2 pt-4 px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="text-xs" data-testid="staff-count-badge">{payroll.length} staff</Badge>
                {data && <span className="text-xs text-muted-foreground" data-testid="payroll-summary">{Number(data.total_hours).toFixed(2)}h · {fmt(data.total_gross)}</span>}
              </div>
              <div className="flex items-center gap-2">
                {/* Pay period toggle */}
                <div className="flex items-center border rounded-md h-8 text-xs" data-testid="pay-period-toggle">
                  <button
                    onClick={() => setPayPeriod('weekly')}
                    className={`px-3 h-full rounded-l-md transition-colors ${payPeriod === 'weekly' ? 'bg-primary text-primary-foreground font-semibold' : 'text-muted-foreground hover:bg-muted/50'}`}
                  >Weekly</button>
                  <button
                    onClick={() => setPayPeriod('monthly')}
                    className={`px-3 h-full rounded-r-md transition-colors border-l ${payPeriod === 'monthly' ? 'bg-primary text-primary-foreground font-semibold' : 'text-muted-foreground hover:bg-muted/50'}`}
                  >Monthly</button>
                </div>
                <div className="flex items-center border rounded-md">
                  <button onClick={prevPeriod} className="p-1.5 hover:bg-muted/50 transition-colors rounded-l-md" title="Previous period" data-testid="prev-week-btn">
                    <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                  </button>
                  <div className="px-3 py-1 flex items-center gap-1.5 border-x min-w-[180px] justify-center">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium" data-testid="week-label">{range.label}</span>
                  </div>
                  <button onClick={nextPeriod} className="p-1.5 hover:bg-muted/50 transition-colors rounded-r-md" title="Next period" data-testid="next-week-btn">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
                <Button variant="outline" size="sm" className="h-8 px-2.5" onClick={load} disabled={loading} title="Force refresh from Trackabi" data-testid="sync-btn">
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                  <span className="text-xs">{loading ? 'Syncing...' : 'Sync'}</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {apiError && !loading && (
              <div className="px-4 sm:px-6 py-4">
                <Alert variant="destructive" data-testid="api-error-alert">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="ml-2">
                    <p className="text-sm font-medium">Trackabi Sync Failed</p>
                    <p className="text-xs mt-1">{apiError}</p>
                    <Button variant="outline" size="sm" className="mt-2 h-7 text-xs" onClick={load}>
                      <RefreshCw className="h-3 w-3 mr-1" />Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {data?.warnings?.length > 0 && !loading && (
              <div className="px-4 sm:px-6 pt-2">
                <Alert className="border-yellow-200 bg-yellow-50" data-testid="warnings-alert">
                  <Info className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="ml-2 text-xs text-yellow-700">
                    {data.warnings.map((w, i) => <p key={i}>{w}</p>)}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {loading ? (
              <div className="p-10 text-center space-y-3">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Syncing with Trackabi...</p>
              </div>
            ) : !apiError && payroll.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-sm text-muted-foreground">No time entries for this week</p>
                <p className="text-xs text-muted-foreground mt-1">Try selecting a different week or check if Trackabi API key is current.</p>
              </div>
            ) : !apiError && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="min-w-[180px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Staff Member</TableHead>
                      <TableHead className="w-[100px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hours</TableHead>
                      <TableHead className="w-[100px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Gross</TableHead>
                      {hasCommissionStaff && (
                        <TableHead className="w-[110px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sales</TableHead>
                      )}
                      {hasCommissionStaff && (
                        <TableHead className="w-[110px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Net Pay</TableHead>
                      )}
                      <TableHead className="min-w-[90px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Work</TableHead>
                      <TableHead className="w-[140px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</TableHead>
                      <TableHead className="w-[60px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payroll.map(p => {
                      const isExpanded = expandedId === p.trackabi_id;
                      const changeIcon = p.pct_change > 0 ? <ArrowUp className="h-3 w-3 text-emerald-600 inline" /> : p.pct_change < 0 ? <ArrowDown className="h-3 w-3 text-red-500 inline" /> : null;
                      const hasAdjustments = p.one_time_total > 0 || p.recurring_total > 0 || p.clawback_total > 0;
                      const salesSum = (p.one_time_total || 0) + (p.recurring_total || 0) - (p.clawback_total || 0);
                      // Adjust standard hours for monthly: (weekly_std / 7) * days_in_period
                      const displayStdHours = payPeriod === 'monthly'
                        ? Math.round((p.standard_hours / 7) * range.days)
                        : p.standard_hours;
                      const colSpan = hasCommissionStaff ? 8 : 6;

                      return (
                        <React.Fragment key={p.trackabi_id || p.email}>
                          <TableRow
                            className={`transition-colors ${p.hours === 0 ? 'opacity-40' : 'hover:bg-[var(--row-hover)]'}`}
                            data-testid={`payroll-row-${p.trackabi_id || p.email}`}
                          >
                            {/* STAFF MEMBER */}
                            <TableCell>
                              <span className="text-sm font-medium" data-testid={`staff-name-${p.trackabi_id}`}>
                                {p.name}
                              </span>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className={`text-[10px] ${p.rate_source === 'missing' ? 'text-red-500 font-medium' : 'text-muted-foreground'}`} data-role="mono">
                                  {p.rate > 0 ? `${fmt(p.rate)}/hr` : '$0.00 (Rate Missing)'}
                                </span>
                                {p.rate_source === 'trackabi' && <Badge variant="outline" className="text-[8px] px-1 py-0">trackabi</Badge>}
                                {p.role && p.role !== 'Other' && <Badge variant="outline" className="text-[9px]">{p.role}</Badge>}
                              </div>
                            </TableCell>

                            {/* HOURS */}
                            <TableCell className="text-right">
                              <span className="text-sm font-bold block" data-role="mono" data-testid={`hours-${p.trackabi_id}`}>{p.hours.toFixed(2)}h</span>
                              <span className="text-[10px] text-muted-foreground">/ {displayStdHours}h std</span>
                              {p.pct_change !== 0 && (
                                <span className={`text-[10px] block ${p.pct_change > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                  {changeIcon} {p.pct_change > 0 ? '+' : ''}{p.pct_change}%
                                </span>
                              )}
                            </TableCell>

                            {/* GROSS */}
                            <TableCell className="text-right">
                              <span className="text-sm font-bold" data-role="mono" data-testid={`wage-${p.trackabi_id}`}>{fmt(p.gross_wage)}</span>
                            </TableCell>

                            {/* SALES — $ button + live math */}
                            {hasCommissionStaff && (
                              <TableCell className="text-right">
                                {p.is_commission_eligible ? (
                                  <div className="flex items-center justify-end gap-1.5">
                                    <span
                                      className={`text-sm font-bold ${salesSum > 0 ? 'text-emerald-600' : salesSum < 0 ? 'text-red-500' : 'text-muted-foreground'}`}
                                      data-role="mono"
                                      data-testid={`sales-${p.trackabi_id}`}
                                    >
                                      {salesSum > 0 ? '+' : ''}{fmt(salesSum)}
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-muted-foreground hover:text-primary flex-shrink-0"
                                      onClick={() => openCommissions(p)}
                                      data-testid={`comm-btn-${p.trackabi_id}`}
                                      title="Manage commissions & clawbacks"
                                    >
                                      <DollarSign className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                            )}

                            {/* NET PAY + clawback icons stacked underneath */}
                            {hasCommissionStaff && (
                              <TableCell className="text-right">
                                {p.is_commission_eligible ? (
                                  <div className="flex flex-col items-end">
                                    <TooltipProvider delayDuration={150}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className={`text-sm font-bold cursor-default inline-block ${hasAdjustments ? 'text-primary underline decoration-dotted decoration-primary/30 underline-offset-2' : ''}`} data-role="mono" data-testid={`net-${p.trackabi_id}`}>
                                            {fmt(p.net_amount)}
                                          </span>
                                        </TooltipTrigger>
                                        {hasAdjustments && (
                                          <TooltipContent side="top" align="end" sideOffset={8} className="bg-slate-900 border-slate-700 text-xs space-y-1 py-2.5 px-3.5 shadow-xl">
                                            <p className="font-semibold text-slate-300 mb-1.5 text-[11px]">Pay Breakdown</p>
                                            <p className="text-slate-200">Gross: <span className="font-semibold text-white">{fmt(p.gross_wage)}</span></p>
                                            {p.one_time_total > 0 && <p className="text-emerald-400">One-Time: <span className="font-semibold">+{fmt(p.one_time_total)}</span></p>}
                                            {p.recurring_total > 0 && <p className="text-sky-400">Recurring: <span className="font-semibold">+{fmt(p.recurring_total)}</span></p>}
                                            {p.clawback_total > 0 && <p className="text-red-400">Clawback: <span className="font-semibold">-{fmt(p.clawback_total)}</span>{p.clawback_capped && <span className="text-amber-400 text-[10px] ml-1">(capped)</span>}</p>}
                                            <hr className="my-1.5 border-slate-600" />
                                            {p.clawback_capped && <p className="text-amber-400 text-[10px] italic mb-1">Clawback capped — commissions only</p>}
                                            <p className="font-bold text-white text-[11px]">Net Pay: {fmt(p.net_amount)}</p>
                                          </TooltipContent>
                                        )}
                                      </Tooltip>
                                    </TooltipProvider>
                                    {(p.clawback_items || []).length > 0 && (
                                      <div className="flex items-center justify-end gap-0.5 mt-0.5">
                                        {(p.clawback_items || []).map((cb, i) => (
                                          <TooltipProvider key={`cb-${i}`} delayDuration={100}>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-red-100 text-red-600 cursor-default flex-shrink-0" data-testid={`clawback-icon-${p.trackabi_id}-${i}`}>
                                                  <AlertTriangle className="h-2.5 w-2.5" />
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" sideOffset={6} className="bg-slate-900 border-slate-700 text-xs py-2 px-3 shadow-xl space-y-0.5">
                                                <p className="text-red-400 font-semibold">-{fmt(cb.amount_per_cycle)}/wk</p>
                                                <p className="text-slate-300">{cb.cycles_remaining} week{cb.cycles_remaining !== 1 ? 's' : ''} remaining</p>
                                                {cb.reason && <p className="text-slate-400 italic">{cb.reason}</p>}
                                                {cb.take_from_commissions_only && <p className="text-amber-400 text-[10px]">Comm. Only</p>}
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm font-bold" data-role="mono">{fmt(p.gross_wage)}</span>
                                )}
                              </TableCell>
                            )}

                            {/* WORK */}
                            <TableCell>
                              {p.tasks.length > 0 ? (
                                <button onClick={() => setExpandedId(isExpanded ? null : p.trackabi_id)}
                                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
                                  data-testid={`tasks-toggle-${p.trackabi_id}`}
                                >
                                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  {p.tasks.length} task{p.tasks.length !== 1 ? 's' : ''}
                                </button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>

                            {/* STATUS — operational badges only */}
                            <TableCell>
                              <div className="flex items-center gap-1 flex-wrap">
                                {p.alerts.map((a, i) => <AlertBadge key={i} alert={a} />)}
                              </div>
                            </TableCell>

                            {/* APPROVE / PAID / ROLLBACK */}
                            <TableCell>
                              {p.is_paid ? (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800 text-white"
                                  onClick={() => {
                                    setRollbackTarget({ email: p.email, name: p.name, week_end: data?.week_end || '' });
                                    setRollbackInput('');
                                  }}
                                  data-testid={`paid-badge-${p.trackabi_id}`}
                                >
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Paid
                                </Button>
                              ) : approvedSet.has(p.email) ? (
                                <div className="flex items-center gap-1">
                                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid={`approved-badge-${p.trackabi_id}`}>
                                    <CheckCircle2 className="h-3 w-3 mr-1" />Approved
                                  </Badge>
                                  {wiseErrors[p.email] && (
                                    <TooltipProvider delayDuration={100}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-100 text-red-600 cursor-default" data-testid={`wise-error-${p.trackabi_id}`}>
                                            <AlertCircle className="h-3 w-3" />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" sideOffset={6} className="bg-slate-900 border-slate-700 text-xs py-2 px-3 shadow-xl max-w-[300px]">
                                          <p className="text-red-400 font-semibold mb-1">Wise Transfer Failed</p>
                                          <p className="text-slate-300 break-words">{wiseErrors[p.email]}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                  <button
                                    onClick={() => handleUndo(p)}
                                    disabled={undoingEmail === p.email}
                                    className="inline-flex items-center justify-center h-5 w-5 rounded-full text-muted-foreground/50 hover:text-red-500 hover:bg-red-50 transition-colors"
                                    title="Undo approval"
                                    data-testid={`undo-btn-${p.trackabi_id}`}
                                  >
                                    {undoingEmail === p.email ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                                  </button>
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                                  onClick={() => handleApprove(p)}
                                  disabled={approvingEmail === p.email}
                                  data-testid={`approve-btn-${p.trackabi_id}`}
                                >
                                  {approvingEmail === p.email ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                                  Approve
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={colSpan} className="bg-muted/30 px-6 py-3">
                                <p className="text-[10px] font-semibold uppercase text-muted-foreground mb-1.5">Work Summary</p>
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                  {p.tasks.map((t, i) => (
                                    <p key={i} className="text-xs text-foreground">{t}</p>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {payroll.length > 0 && !apiError && (() => {
              const totalAdj = payroll.reduce((s, p) => s + (p.one_time_total || 0) + (p.recurring_total || 0) - (p.clawback_total || 0), 0);
              const totalNet = payroll.reduce((s, p) => s + (p.net_amount || p.gross_wage || 0), 0);
              const adjSign = totalAdj >= 0 ? '+' : '';
              const approvedCount = payroll.filter(p => approvedSet.has(p.email) && !p.is_paid).length;
              // Break out Gross by pay type (Wise, Bank Transfer, Alleanza, etc.)
              const grossByMethod = {};
              payroll.forEach(p => {
                const m = p.payment_method || 'wise';
                grossByMethod[m] = (grossByMethod[m] || 0) + (p.gross_wage || 0);
              });
              const grossMethodEntries = Object.entries(grossByMethod)
                .filter(([, v]) => v > 0)
                .sort((a, b) => b[1] - a[1]);
              return (
                <div className="px-4 sm:px-6 py-2.5 border-t bg-muted/30 text-xs">
                  <div className="flex items-center gap-5 flex-wrap">
                    <span className="text-muted-foreground">Total Hours: <span className="font-bold text-foreground text-sm" data-role="mono" data-testid="total-hours">{Number(data?.total_hours || 0).toFixed(2)}h</span></span>
                    <span className="text-muted-foreground">Gross: <span className="font-bold text-primary text-sm" data-role="mono" data-testid="total-gross">{fmt(data?.total_gross)}</span></span>
                    {hasCommissionStaff && (
                      <span className="text-muted-foreground">Commissions: <span className={`font-bold text-sm ${totalAdj > 0 ? 'text-emerald-600' : totalAdj < 0 ? 'text-red-500' : 'text-muted-foreground'}`} data-role="mono" data-testid="total-adj">{totalAdj !== 0 ? `${adjSign}${fmt(totalAdj)}` : fmt(0)}</span></span>
                    )}
                    <span className="text-muted-foreground">Net Liability: <span className="font-bold text-foreground text-sm" data-role="mono" data-testid="total-net">{fmt(totalNet)}</span></span>
                    <div className="ml-auto flex items-center gap-3">
                      {approvedCount > 0 && (
                        <span className="text-[11px] text-emerald-600 font-medium">{approvedCount} approved</span>
                      )}
                      <Button
                        size="sm"
                        className="h-8 px-4 text-xs font-semibold"
                        onClick={() => setConfirmModalOpen(true)}
                        disabled={wiseSending || approvedCount === 0}
                        data-testid="send-payment-btn"
                      >
                        {wiseSending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                        Send for Payment
                      </Button>
                    </div>
                  </div>
                  {grossMethodEntries.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/50 flex items-center gap-2 flex-wrap" data-testid="gross-by-paytype">
                      <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Gross by Pay Type</span>
                      {grossMethodEntries.map(([method, amount]) => (
                        <span
                          key={method}
                          className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2 py-1"
                          data-testid={`gross-method-${method}`}
                        >
                          <span className="text-[11px] font-medium text-muted-foreground">{PAY_METHOD_LABELS[method] || method}</span>
                          <span className="text-xs font-bold text-foreground" data-role="mono">{fmt(amount)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
        </div>
      </main>

      <StaffModal open={staffModalOpen} onOpenChange={setStaffModalOpen} />
      <AlleanzaSettingsModal open={alleanzaOpen} onOpenChange={setAlleanzaOpen} />
      <CommissionModal
        open={commModalOpen}
        onOpenChange={setCommModalOpen}
        staff={commStaff}
        staffEmail={commStaff?.email || ''}
        staffName={commStaff?.name || ''}
        onUpdated={load}
        onNotesSaved={load}
      />

      {/* Payment Confirmation Modal */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="confirm-modal-title">
              <Send className="h-5 w-5 text-primary" />
              Confirm Payment Batch
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <p className="text-xs text-muted-foreground">The following payouts will be processed for {range.label}:</p>
            <Separator />
            {(() => {
              const approvedRows = payroll.filter(p => approvedSet.has(p.email) && !p.is_paid && (p.net_amount || p.gross_wage) > 0);
              const groups = {};
              approvedRows.forEach(p => {
                const method = p.payment_method || 'wise';
                if (!groups[method]) groups[method] = { count: 0, total: 0 };
                groups[method].count += 1;
                groups[method].total += (p.net_amount || p.gross_wage);
              });
              const grandTotal = approvedRows.reduce((s, p) => s + (p.net_amount || p.gross_wage), 0);
              return (
                <div className="space-y-2">
                  {Object.entries(groups).map(([method, { count, total }]) => (
                    <div key={method} className="flex items-center justify-between py-1.5 px-3 rounded bg-muted/50">
                      <div>
                        <span className="text-sm font-medium">{PAY_METHOD_LABELS[method] || method}</span>
                        <span className="text-xs text-muted-foreground ml-2">({count} staff)</span>
                      </div>
                      <span className="text-sm font-bold" data-role="mono">{fmt(total)}</span>
                    </div>
                  ))}
                  <Separator />
                  <div className="flex items-center justify-between px-3">
                    <span className="text-sm font-semibold">Total</span>
                    <span className="text-base font-bold text-primary" data-role="mono">{fmt(grandTotal)}</span>
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmModalOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSendForPayment} disabled={wiseSending} data-testid="confirm-process-btn">
              {wiseSending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Confirm & Process
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Modal */}
      <Dialog open={!!rollbackTarget} onOpenChange={(o) => { if (!o) { setRollbackTarget(null); setRollbackInput(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600" data-role="heading">Rollback Payroll</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="p-3 rounded-md bg-red-50 border border-red-200">
              <p className="text-sm font-semibold text-red-800">DANGER</p>
              <p className="text-xs text-red-600 mt-1">
                Rolling this back will NOT reverse bank transfers or emails. It only resets their dashboard state to Draft.
              </p>
            </div>
            {rollbackTarget && (
              <>
                <div className="text-sm">
                  <span className="text-muted-foreground">Staff:</span> <span className="font-semibold">{rollbackTarget.name}</span>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">Period:</span> <span className="font-semibold">{rollbackTarget.week_end}</span>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Type exactly: <code className="bg-muted px-1 py-0.5 rounded text-xs">rollback {rollbackTarget.name} {rollbackTarget.week_end}</code>
                  </label>
                  <Input
                    value={rollbackInput}
                    onChange={(e) => setRollbackInput(e.target.value)}
                    placeholder="Type confirmation..."
                    className="h-9 text-sm font-mono"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setRollbackTarget(null); setRollbackInput(''); }}>Cancel</Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={
                rollbackSending ||
                !rollbackTarget ||
                rollbackInput !== `rollback ${rollbackTarget?.name} ${rollbackTarget?.week_end}`
              }
              onClick={async () => {
                if (!rollbackTarget) return;
                setRollbackSending(true);
                try {
                  await api.post('/api/payroll/rollback', {
                    staff_email: rollbackTarget.email,
                    week_end: rollbackTarget.week_end,
                    staff_name: rollbackTarget.name,
                  });
                  toast.success(`Rolled back ${rollbackTarget.name} to Draft`);
                  setRollbackTarget(null);
                  setRollbackInput('');
                  load();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Rollback failed');
                } finally {
                  setRollbackSending(false);
                }
              }}
            >
              {rollbackSending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Confirm Rollback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
